import * as React from "react";
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui";

import { cn } from "@shared/lib/cn";
import { GrowLayer } from "./grow-layer";
import { MenuPlate } from "./menu-plate";
import {
  MENU_CONTENT,
  MENU_ITEM,
  MENU_LABEL,
  MENU_SEPARATOR,
  MENU_SHORTCUT,
  SUB_CONTENT,
  SUB_TRIGGER,
} from "./menu-shared";

/*
 * Меню.
 *
 * Вид тот же, что у списка выбора (`select.tsx`), и это не совпадение: во
 * всплывающем списке система знает один вид, и второго заводить не за чем.
 * Описан он теперь здесь — а `.menu` в `ui.css` описывал то же самое второй
 * раз, и копии успели разойтись: зазор в пункте был 8px против 5.6, подсветка
 * строки — `--ui-pick-bg` против `--ui-active`. Ловится такое только замером,
 * и то если догадаться сравнить.
 *
 * Значки берутся из `ui/icons`, а не из `lucide-react` напрямую: размер и
 * толщина линии настроены там под плотность системы.
 */
const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

function DropdownMenuContent({
  className,
  children,
  sideOffset = 0,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        /* Тот же слой, что у списка выбора: порядок задаёт порядок в DOM. */
        className={cn(MENU_CONTENT, className)}
        {...props}
      >
        <GrowLayer />
        <MenuPlate />
        {children}
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  );
}

/**
 * Заголовок меню: чему оно принадлежит.
 *
 * Прописными и в разрядку — как все надписи такого рода в системе: это не
 * пункт, а подпись к набору, и читаться она должна иначе.
 */
function DropdownMenuLabel({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label>) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      className={cn(MENU_LABEL, className)}
      {...props}
    />
  );
}

function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn(MENU_SEPARATOR, className)}
      {...props}
    />
  );
}

function DropdownMenuItem({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  variant?: "default" | "destructive";
}) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-variant={variant}
      className={cn(MENU_ITEM, className)}
      {...props}
    />
  );
}

/** Правая подпись пункта: горячая клавиша, текущее значение, слово «право». */
function DropdownMenuShortcut({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(MENU_SHORTCUT, className)}
      {...props}
    />
  );
}

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
};

/*
 * Вложенное меню.
 *
 * Нужно там, где пункт не действие, а выбор из списка: «переместить в» с
 * перечнем категорий. Раньше это делалось подменой — первое меню закрывалось,
 * второе открывалось в той же точке, — и путь назад терялся: закрыв второе,
 * человек оказывался нигде, а не там, откуда пришёл.
 */
function DropdownMenuSub(props: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>) {
  return <DropdownMenuPrimitive.Sub data-slot="dropdown-menu-sub" {...props} />;
}

function DropdownMenuSubTrigger({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger>) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      data-slot="dropdown-menu-sub-trigger"
      className={cn(SUB_TRIGGER, className)}
      {...props}
    />
  );
}

function DropdownMenuSubContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.SubContent
        data-slot="dropdown-menu-sub-content"
        className={cn(SUB_CONTENT, className)}
        {...props}
      >
        <GrowLayer from="point" />
        <MenuPlate />
        {children}
      </DropdownMenuPrimitive.SubContent>
    </DropdownMenuPrimitive.Portal>
  );
}

export {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
};
